// src/components/common/ErrorBoundary.js
// Filet de securite contre la page blanche.
//
// En React, une erreur non rattrapee pendant le rendu demonte TOUT l'arbre :
// l'ecran devient blanc, sans le moindre message. Le detail n'existe que dans
// la console du navigateur, ce que personne ne pense a ouvrir — et sur
// telephone, ce n'est meme pas accessible.
//
// Ce composant intercepte l'erreur, affiche ce qui s'est passe et permet de
// revenir en arriere. Il ne CORRIGE aucun bug : il rend visible celui qui se
// produit, au lieu de le laisser se manifester par un ecran vide.
//
// Maxula — AbodyStudio Limited

import React from 'react';
import { FiAlertTriangle, FiRefreshCw, FiHome, FiCopy } from 'react-icons/fi';

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // La console reste alimentee : c'est la que se trouve la pile complete,
    // avec les numeros de ligne, quand on debogue sur ordinateur.
    // eslint-disable-next-line no-console
    console.error('[Maxula] Erreur de rendu :', error, info);
  }

  componentDidUpdate(prevProps) {
    // Changement de page : on efface l'erreur. Sans cela, une seule page en
    // echec bloquerait toute l'application jusqu'au rechargement complet,
    // meme sur des ecrans parfaitement sains.
    if (prevProps.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null, info: null, copied: false });
    }
  }

  copyDetails = () => {
    const { error, info } = this.state;
    const text = [
      `Page : ${typeof window !== 'undefined' ? window.location.href : ''}`,
      `Erreur : ${error?.message || error}`,
      '',
      String(error?.stack || ''),
      '',
      String(info?.componentStack || ''),
    ].join('\n');
    try {
      navigator.clipboard.writeText(text);
      this.setState({ copied: true });
    } catch (e) {
      this.setState({ copied: false });
    }
  };

  render() {
    const { error, info, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
        <div className="max-w-xl w-full bg-white border border-red-100 rounded-2xl shadow-lg p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-red-500 shrink-0">
              <FiAlertTriangle size={26} />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900">
                Cette page n'a pas pu s'afficher
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Le reste du site fonctionne normalement. Vous pouvez recharger ou
                revenir à l'accueil.
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3">
            <div className="text-xs font-semibold text-gray-500 mb-1">
              Détail technique
            </div>
            <div className="text-[13px] text-red-700 break-words font-mono">
              {String(error?.message || error)}
            </div>
            {info?.componentStack ? (
              <pre className="mt-2 max-h-40 overflow-auto text-[11px] leading-relaxed text-gray-500 whitespace-pre-wrap">
                {String(info.componentStack).trim().split('\n').slice(0, 8).join('\n')}
              </pre>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 rounded-lg bg-[#e11d7b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#c4176a]"
            >
              <FiRefreshCw size={15} /> Recharger la page
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/'; }}
              className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <FiHome size={15} /> Accueil
            </button>
            {/* Bouton de copie : c'est ce texte qu'on demande a l'utilisateur
                d'envoyer au support. Lui faire retaper un message d'erreur a la
                main ne donne jamais rien d'exploitable. */}
            <button
              type="button"
              onClick={this.copyDetails}
              className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <FiCopy size={15} /> {copied ? 'Copié' : 'Copier le détail'}
            </button>
          </div>

          <p className="mt-3 text-xs text-gray-400">
            Support : Support@abodystudio.com — WhatsApp +212663033383
          </p>
        </div>
      </div>
    );
  }
}

// La classe ne peut pas utiliser de hook : on lui passe la position courante en
// prop pour qu'elle sache quand se reinitialiser.
const ErrorBoundary = ({ routeKey, children }) => (
  <ErrorBoundaryInner routeKey={routeKey}>{children}</ErrorBoundaryInner>
);

export default ErrorBoundary;
